import { Grid, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import FormGridRow from "../../../../../common/components/functional/formGridRow/FormGridRow";
import { useConfirmationDialog } from "../../../../../contexts/ConfirmationDialogContext";
import {
  selectTargetedShiftValidationAssignment,
  setShiftTargetingRules,
  updateTargetedShiftSpec,
} from "../../../../employment/employmentSlice";
import { normalizeSalaryRows } from "../../../../employment/targetedShiftSalaries/helpers";
import {
  TargetedShiftSalaryRow,
  TargetedShiftSpec,
} from "../../../../employment/targetedShiftSalaries/targetedShiftSalariesApi";
import {
  fetchAssignmentsByTalentId,
  selectAssignmentsByEmploymentId,
} from "../../AssignmentAdditionalInfo/AssignmentsSlice";
import { RuleEditDialog, RuleFormValues } from "./RuleEditDialog";
import { isCompanyOnlyRule } from "./ruleHelpers";
import { RulesTable } from "./RulesTable";
import { FormValues, TargetingRuleValues } from "./TargetedShifts";

import { CustomButton } from "../../../../../common/components/functional/customButton/CustomButton";
import { fetchCustomerCompanyByOrgId } from "../../../../customer/customerOrganizationsSlice";
import { AccessRightContext } from "../../../../user/AccessRightProvider";
import { AssignmentRow } from "./AssignmentRow";
import { AssignmentSelectDialog } from "./AssignmentSelectDialog";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { FlexVerticalAligned } from "../../../../../common/components/styled/styled";
import { fetchCanSetTargetingForEmployment } from "../../../../employment/targetedShiftSalaries/targetedShiftSalariesActions";
import useMessage from "../../../../../hooks/useMessage";

type Props = {
  onRulesChanged?: (count: number) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toTargetingRule = (r: any) => ({
  custCompanyId: r.customerCompany?.id || "",
  custOrgs: r.customerOrganization ? [r.customerOrganization.id] : [],
  workLocs: (r.workLocations ?? []).map((wl: { id: string }) => wl.id).sort(),
  workTitles: (r.workTitles ?? []).map((wt: { id: string }) => wt.id).sort(),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildUpdatedSpec = (
  newRules: any[],
  newVersion: number,
  salaries: TargetedShiftSalaryRow[],
  selectedAssignmentId?: string,
): TargetedShiftSpec => ({
  version: newVersion,
  targetingRules: newRules.map((r) => ({
    custCompany: {
      id: r.customerCompany?.id || "",
      name: r.customerCompany?.name || "",
    },
    custOrgs: r.customerOrganization
      ? [
          {
            id: r.customerOrganization.id,
            name: r.customerOrganization.name || "",
          },
        ]
      : [],
    workLocs: (r.workLocations ?? []).map((wl: any) => ({
      id: wl.id,
      name: wl.name || "",
    })),
    workTitles: (r.workTitles ?? []).map((wt: any) => wt.id),
  })),
  salaries: normalizeSalaryRows(salaries),
  variationValidationAssignmentId: selectedAssignmentId || null,
});

export const TargetedShiftsForm = ({ onRulesChanged }: Props) => {
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();
  const { t } = useTranslation();
  const { employmentId, talentId } = useParams();
  const { isAdmin } = useContext(AccessRightContext);
  const { confirm } = useConfirmationDialog();
  const { tes } = useAppSelector((state) => state.localized);
  const employment = useAppSelector((state) => state.employment.additionalInfo);

  const { control, watch, setValue, getValues } = useFormContext<FormValues>();
  const selectedAssignmentId = watch("selectedAssignment");
  const version = watch("version");

  const reduxSpecVersion = useAppSelector(
    (s) => s.employment.additionalInfo?.targetedShiftSpec?.version ?? 0,
  );
  const prevReduxSpecVersionRef = useRef(reduxSpecVersion);

  useEffect(() => {
    const prev = prevReduxSpecVersionRef.current;
    prevReduxSpecVersionRef.current = reduxSpecVersion;
    if (reduxSpecVersion > prev) {
      const currentFormVersion = getValues("version");
      if (reduxSpecVersion > currentFormVersion) {
        setValue("version", reduxSpecVersion);
      }
    }
  }, [reduxSpecVersion, getValues, setValue]);

  const {
    fields: rules,
    append,
    remove,
    update,
    replace,
  } = useFieldArray({
    control,
    name: "rules",
  });

  const assignments = useAppSelector(
    selectAssignmentsByEmploymentId(employmentId ?? ""),
  );
  const selectedAssignment = assignments.find(
    (a) => a.assignmentId === selectedAssignmentId,
  );
  // Assignments created by merging a short shift order into this long
  // employment (targeted shifts) must not be selectable as the employment's
  // own default/validation assignment - only its genuine long assignments are.
  const longAssignments = useMemo(
    () => assignments.filter((a) => !a.isTargetedShift),
    [assignments],
  );

  const [isRulesTableOpen, setIsRulesTableOpen] = useState(true);
  const [isAssignmentSelectDialogOpen, setIsAssignmentSelectDialogOpen] =
    useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<TargetingRuleValues | null>(
    null,
  );

  const defaultRuleCustomerCompany =
    rules.find((r) => r.isDefaultRule)?.customerCompany ?? null;

  const ruleInitialValues: Partial<RuleFormValues> | undefined = useMemo(
    () =>
      selectedRule
        ? {
            customerCompany: selectedRule?.customerCompany || null,
            customerOrganization: selectedRule?.customerOrganization || null,
            workTitles: selectedRule?.workTitles ?? [],
            workLocations: selectedRule?.workLocations ?? [],
            isDefaultRule: selectedRule?.isDefaultRule ?? false,
          }
        : undefined,
    [selectedRule],
  );

  const checkIsAllowedToSetTargeting = useCallback(
    async (assignmentId: string, employmentId: string) =>
      dispatch(
        fetchCanSetTargetingForEmployment({
          employmentId,
          validationAssignmentId: assignmentId,
        }),
      ).then((action) => (isFulfilled(action) ? action.payload : false)),
    [dispatch],
  );

  const handleAssignmentChange = useCallback(
    async (assignmentId: string) => {
      if (
        assignmentId === selectedAssignmentId ||
        !assignmentId ||
        !employmentId
      )
        return;
      const isAllowed = await checkIsAllowedToSetTargeting(
        assignmentId,
        employmentId,
      );
      if (!isAllowed) {
        sendMessage(
          t("order:cannotCreateDuplicateTargetedEmployment"),
          "error",
        );
        return;
      }
      const assignment = assignments.find(
        (a) => a.assignmentId === assignmentId,
      );
      if (!assignment) return;
      dispatch(
        fetchCustomerCompanyByOrgId({
          customerOrgId: assignment.details.customerOrgId,
        }),
      ).then(async (companyAction) => {
        const company = isFulfilled(companyAction)
          ? companyAction.payload
          : null;
        const newDefaultRule: TargetingRuleValues = {
          customerCompany: {
            id: company?.customerCompanyId || "",
            name: company?.name || "",
          },
          customerOrganization: {
            id: assignment.details.customerOrgId,
            name: assignment.details.customerOrgName,
          },
          workLocations: [],
          workTitles: [],
          isDefaultRule: true,
        };
        const assignmentAction = await dispatch(
          selectTargetedShiftValidationAssignment({
            employmentId: employmentId ?? "",
            validationAssignmentId: assignmentId,
            version,
          }),
        );
        if (isFulfilled(assignmentAction)) {
          setValue("selectedAssignment", assignmentId, { shouldDirty: true });
          const assignmentActionNewVersion =
            assignmentAction.payload[0]?.version ?? version;
          const action = await dispatch(
            setShiftTargetingRules({
              employmentId: employmentId ?? "",
              version: assignmentActionNewVersion,
              rules: [toTargetingRule(newDefaultRule)],
            }),
          );
          if (isFulfilled(action)) {
            const newVersion =
              action.payload[0]?.version ?? assignmentActionNewVersion;
            setValue("version", newVersion);
            replace([newDefaultRule]);
            dispatch(
              updateTargetedShiftSpec(
                buildUpdatedSpec(
                  [newDefaultRule],
                  newVersion,
                  employment?.targetedShiftSpec?.salaries ?? [],
                  assignmentId,
                ),
              ),
            );
            onRulesChanged?.(1);
          }
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      assignments,
      setValue,
      dispatch,
      employmentId,
      version,
      onRulesChanged,
      replace,
      employment?.targetedShiftSpec?.salaries,
      selectedAssignmentId,
    ],
  );

  const handleAddClick = () => {
    setSelectedRule(null);
    setEditDialogOpen(true);
  };

  const handleEditClick = (rule: TargetingRuleValues) => {
    setSelectedRule(rule);
    setEditDialogOpen(true);
  };

  const handleRemoveClick = async (id: string) => {
    const index = rules.findIndex((r) => r.id === id);
    if (index === -1) return;

    const remainingRules = rules.filter((r) => r.id !== id);
    const action = await dispatch(
      setShiftTargetingRules({
        employmentId: employmentId ?? "",
        version,
        rules: remainingRules.map(toTargetingRule),
      }),
    );
    if (isFulfilled(action)) {
      setValue("version", action.payload[0].version);
      remove(index);
      dispatch(
        updateTargetedShiftSpec(
          buildUpdatedSpec(
            remainingRules,
            action.payload[0].version,
            employment?.targetedShiftSpec?.salaries ?? [],
            selectedAssignmentId,
          ),
        ),
      );
      onRulesChanged?.(remainingRules.length);
    }
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedRule(null);
  };

  const handleOneRuleSave = async (values: RuleFormValues): Promise<void> => {
    if (isCompanyOnlyRule(values)) {
      const confirmed = await confirm({
        title: t("talentProfile:targetedShifts.companyOnlyRuleTitle"),
        contentText: t("talentProfile:targetedShifts.companyOnlyRuleInfo"),
        confirmText: t("common:confirm"),
        cancelText: t("common:cancel"),
      });
      if (!confirmed) return;
    }

    let updatedRules: TargetingRuleValues[];
    let updateIndex = -1;

    if (selectedRule) {
      updateIndex = rules.findIndex((r) => r.id === selectedRule.id);
      updatedRules = rules.map((r) =>
        r.id === selectedRule.id ? { ...r, ...values } : r,
      );
    } else {
      updatedRules = [...rules, { ...values, id: Date.now().toString() }];
    }

    const action = await dispatch(
      setShiftTargetingRules({
        employmentId: employmentId ?? "",
        version,
        rules: updatedRules.map(toTargetingRule),
      }),
    );

    if (isFulfilled(action)) {
      setValue("version", action.payload[0].version);
      if (selectedRule && updateIndex !== -1) {
        update(updateIndex, { ...rules[updateIndex], ...values });
      } else {
        append({ ...values, id: Date.now().toString() });
      }
      dispatch(
        updateTargetedShiftSpec(
          buildUpdatedSpec(
            updatedRules,
            action.payload[0].version,
            employment?.targetedShiftSpec?.salaries ?? [],
            selectedAssignmentId,
          ),
        ),
      );
      onRulesChanged?.(updatedRules.length);
      handleDialogClose();
    }
  };

  useEffect(() => {
    if (!talentId) return;
    dispatch(fetchAssignmentsByTalentId({ talentId }));
  }, [dispatch, talentId]);

  return (
    <>
      <Grid item xs={12}>
        <Typography>
          {t("talentProfile:targetedShifts.assignmentTargeting")}
        </Typography>
      </Grid>
      <Grid item xs={12} sx={{ fontSize: "14px", pt: 0.5, pb: 1 }}>
        {selectedAssignment ? (
          // Let only admins change assignment after rules are created
          <AssignmentRow
            assignment={selectedAssignment}
            canEdit={isAdmin || version === 0}
            openSelectDialog={() => setIsAssignmentSelectDialogOpen(true)}
          />
        ) : (
          <CustomButton
            variant="outlined"
            color="error"
            sx={{ borderRadius: "24px", mt: 1, mb: 2 }}
            onClick={() => setIsAssignmentSelectDialogOpen(true)}
          >
            {t("talentProfile:targetedShifts.selectAssignment")}
          </CustomButton>
        )}
      </Grid>
      <FormGridRow
        label={t("payslips:eezyCompany")}
        labelSize={2}
        fieldSize={10}
        GridFieldProps={{ pl: 2 }}
      >
        <Typography>{employment?.details?.employerName}</Typography>
      </FormGridRow>
      <FormGridRow
        label={t("common:tes")}
        labelSize={2}
        fieldSize={10}
        GridFieldProps={{ pl: 2 }}
      >
        <FlexVerticalAligned>
          <Typography>{tes[employment?.tesId || ""]}</Typography>
          <InfoTooltip
            title={t("order:targetingRulesTesInfo")}
            style={{ marginLeft: 3 }}
          />
        </FlexVerticalAligned>
      </FormGridRow>
      <FormGridRow
        label={t("common:customerOrganization")}
        labelSize={2}
        fieldSize={10}
        GridFieldProps={{ pl: 2 }}
      >
        {selectedAssignment ? (
          <Typography>{selectedAssignment.details.customerOrgName}</Typography>
        ) : (
          <Typography color="error">
            {t("talentProfile:targetedShifts.selectAssignment")}
          </Typography>
        )}
      </FormGridRow>
      <Grid item container xs={12} pt={2}>
        <RulesTable
          rules={rules}
          addRow={selectedAssignmentId ? handleAddClick : undefined}
          editRow={handleEditClick}
          deleteRow={handleRemoveClick}
          isCollapsed={!isRulesTableOpen}
          onToggleCollapse={() => setIsRulesTableOpen((prev) => !prev)}
        />
      </Grid>
      <RuleEditDialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        onSave={handleOneRuleSave}
        initialValues={ruleInitialValues}
        contractId={selectedAssignment?.details?.contractId}
        customerCompany={defaultRuleCustomerCompany}
      />
      <AssignmentSelectDialog
        open={isAssignmentSelectDialogOpen}
        onClose={() => setIsAssignmentSelectDialogOpen(false)}
        onSave={handleAssignmentChange}
        selectedAssignmentId={selectedAssignmentId}
        assignmentOptions={longAssignments}
      />
    </>
  );
};
